import { Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MatPaginator, MatSort, PageEvent, Sort } from '@angular/material';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, of, Subscription } from 'rxjs';
import { startWith } from 'rxjs/internal/operators/startWith';
import { catchError, debounceTime, map, switchMap, tap } from 'rxjs/operators';
import { SearchObject } from 'src/app/layout/orders/search-orders/orders-body/orders-body.component';
import { ServiceTemplate } from 'src/app/models/interface/serviceTemplate/serviceTemplate';
import { ServiceTemplateData } from 'src/app/models/interface/serviceTemplate/serviceTemplateData';
import { ServiceTemplateRequest } from 'src/app/models/interface/serviceTemplate/serviceTemplateRequest';
import { CatalogService } from 'src/app/services/api/catalog/catalog.service';
import { NotificationLevel, NotificationsService } from 'src/app/services/api/notifications/notifications.service';
import { BreadcrumbsService } from 'src/app/services/utils/breadcrumbs.service';
import { MAP_TARGET_SERVICE_TEMPLATE_STATUS } from 'src/app/utils/Constants';
import { DialogChangeServiceTemplateStatusComponent } from './dialog-change-service-template-status/dialog-change-service-template-status.component';
import { DialogDeleteServiceTemplateComponent } from './dialog-delete-service-template/dialog-delete-service-template.component';
import { DialogEditServiceTemplateComponent } from './dialog-edit-service-template/dialog-edit-service-template.component';

@Component({
  selector: 'app-search-service-templates',
  templateUrl: './search-service-templates.component.html',
  styleUrls: ['./search-service-templates.component.scss']
})
export class SearchServiceTemplatesComponent implements OnInit {

  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  displayedColumns: string[] = [
    'name',
    'state',
    'version',
    'actions'];
  data: ServiceTemplate[] = [];
  resultsLength = 0;

  loading: boolean;

  newServiceTemplateIsDisabled: boolean;

  private searchSubject = new BehaviorSubject<void>(null);

  private subscriptions = new Subscription();

  form: FormGroup;

  pendingActions = {
    queryTemplates: false,
    dialogChangeStatus: false
  }

  tooltip = {
    refresh: '',
    clear: '',
    edit: '',
    delete: ''
  }

  dialogChangeStatusRef: MatDialogRef<DialogChangeServiceTemplateStatusComponent>;
  dialogDeleteServiceTemplateRef: MatDialogRef<DialogDeleteServiceTemplateComponent>;
  dialogEditServiceTemplateRef: MatDialogRef<DialogEditServiceTemplateComponent>;

  constructor(private translate: TranslateService,
    private breadcrumbsService: BreadcrumbsService,
    private catalogService: CatalogService,
    private dialog: MatDialog,
    private router: Router,
    private notificationsService: NotificationsService) { }

  ngOnInit() {
    this.loading = false;
    this.newServiceTemplateIsDisabled = false;

    this.breadcrumbsService.sendBreadcrumbs(
      {
        breadcrumbs:
          [
            { title: 'Administración', url: 'provision/management' },
            { title: 'ServiceTemplates FTTH', url: 'provision/management/serviceTemplates/search' }
          ]
      }
    );

    this.form = new FormGroup({});
    this.initTooltips();
  }

  private initTooltips() {
    this.translate.get('DUMMY.key').subscribe((translated: string) => {
      this.tooltip.refresh = this.translate.instant('TOOLTIPS.refreshResults');
      this.tooltip.clear = this.translate.instant('TOOLTIPS.clearForm');
      this.tooltip.edit = this.translate.instant('TOOLTIPS.editServiceTemplate');
      this.tooltip.delete = this.translate.instant('TOOLTIPS.deleteServiceTemplate');
    });
  }

  resetForm() {
    this.form.reset();
  }

  search() {
    this.searchSubject.next();
  }

  ngAfterViewInit() {
    this.subscriptions.add(
      combineLatest(
        this.sort.sortChange
          .pipe(
            startWith({
              active: 'createdAt',
              direction: 'desc'
            } as Sort),
            tap(() => this.paginator.firstPage()),
          ),
        this.paginator.page
          .pipe(
            startWith({
              pageIndex: this.paginator.pageIndex,
              previousPageIndex: 0,
              pageSize: this.paginator.pageSize,
              length: this.paginator.length
            } as PageEvent),
          ),
        this.searchSubject.asObservable(),
      )
        .pipe(
          debounceTime(50),
          switchMap(([sort, page]: [Sort, PageEvent, void]) => {
            this.loading = true;
            const search = new SearchObject([]);
            search.setPage(page.pageIndex);
            search.setSize(page.pageSize);
            if (sort.active && sort.direction) {
              search.setSort([{
                _key: sort.active,
                _value: sort.direction.toUpperCase()
              }]);
            } else {
              search.setSort([]);
            }
            return this.catalogService
              .getServiceTemplates(search).pipe(
                catchError(() => of({
                  content: [],
                  totalElements: 0,
                })
                ),
              );
          }),
          map(data => {
            this.loading = false;
            this.resultsLength = data.totalElements;
            return data.content;
          }),
          catchError(() => {
            this.loading = false;
            this.resultsLength = 0;
            return of([]);
          }),
        )
        .subscribe(data => {
          this.data = data
        }));
  }

  public checkButtonStatus(element: ServiceTemplate, target: string) {
    return element.state === target;
  }

  public goToNewServiceTemplate() {
    this.router.navigate([`/provision/management/serviceTemplates/new`, {}]);
  }

  public openDialogChangeStatus(element: ServiceTemplate) {
    this.pendingActions.dialogChangeStatus = true;
    this.dialogChangeStatusRef = this.dialog.open(DialogChangeServiceTemplateStatusComponent, {
      data: {
        element
      }
    });

    this.dialogChangeStatusRef.afterClosed().subscribe((boolChangeStatus) => {
      if (boolChangeStatus) {

        const serviceTemplateRequest: ServiceTemplateRequest = {
          namingId: element.namingId,
          name: element.name,
          version: element.version,
          specId: element.spec_id,
          state: MAP_TARGET_SERVICE_TEMPLATE_STATUS[element.state],
          serviceTemplateCharValues: element.serviceTemplateCharValues
        };

        this.catalogService.updateServiceTemplate(element.namingId, serviceTemplateRequest).subscribe(
          (data) => {
            this.notificationsService.showNotification(`Se ha modificado el estado del ServiceTemplate ${element.name} de forma correcta`, NotificationLevel.SUCCESS);
            this.search();
          },
          (error) => {
            this.notificationsService.showNotification(
              `Ha habido un problema al modificar el estado del serviceTemplate:\n${error.error.errorDesc.errorDesc}`,
              NotificationLevel.ERROR);
          });
      }

      this.pendingActions.dialogChangeStatus = false;
    });
  }

  public editServiceTemplate(element: ServiceTemplate) {
    this.dialogEditServiceTemplateRef = this.dialog.open(DialogEditServiceTemplateComponent, {
      data: {
        element
      }
    });

    this.dialogEditServiceTemplateRef.afterClosed().subscribe(() => {
      this.search();
    });
  }

  public deleteServiceTemplate(element: ServiceTemplate) {
    this.dialogDeleteServiceTemplateRef = this.dialog.open(DialogDeleteServiceTemplateComponent, {
      data: {
        element
      }
    });

    this.dialogDeleteServiceTemplateRef.afterClosed().subscribe((boolDeleteServiceTemplate) => {
      if (boolDeleteServiceTemplate) {
        this.catalogService.deleteServiceTemplate(element.namingId).subscribe(
          (data) => {
            this.notificationsService.showNotification(`ServiceTemplate ${element.name} borrada correctamente`, NotificationLevel.SUCCESS);
            this.search();
          },
          (error) => {
            this.notificationsService.showNotification(
              `Ha habido un problema al borrar el template:\n${error.error.errorDesc.message}`, NotificationLevel.ERROR);
          }
        );
      }
    });
  }

}
